import React, { useMemo, useState } from 'react';
import { Flex, Text } from '@adobe/react-spectrum';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    CartesianGrid,
    Cell,
    Label,
    Legend,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatInt, formatNumber } from '../../common/cardStats/formatters';

/**
 * Manabase role filters surfaced as toggle chips below the color
 * production section. Each entry declares the label + predicate
 * the chip applies to a card's `manabaseRoles` object. Order here
 * is the order they render.
 */
/**
 * `group` partitions chips into "Mana sources" (non-land producers, fast mana)
 * and "Lands" (land subtypes). `icon` picks a permanent-type SVG from
 * /resources/typeIcons/ — matches the iconography used in BaseDeckPane.
 */
const ROLE_FILTERS = [
    // Mana sources
    { key: 'fastMana', label: 'Fast mana', group: 'mana', icon: 'artifact', match: (r) => r.isFastMana },
    { key: 'rocks', label: 'Rocks', group: 'mana', icon: 'artifact', match: (r) => r.manaProducerType === 'rock' },
    { key: 'dorks', label: 'Dorks', group: 'mana', icon: 'creature', match: (r) => r.manaProducerType === 'dork' },
    { key: 'rituals', label: 'Rituals', group: 'mana', icon: 'sorcery', match: (r) => r.manaProducerType === 'ritual' },
    {
        key: 'treasure',
        label: 'Treasure',
        group: 'mana',
        icon: 'artifact',
        match: (r) => r.manaProducerType === 'treasure',
    },
    {
        key: 'landRamp',
        label: 'Land ramp',
        group: 'mana',
        icon: 'sorcery',
        match: (r) => r.manaProducerType === 'landRamp',
    },
    // Lands
    { key: 'basics', label: 'Basics', group: 'land', icon: 'land', match: (r) => r.landType === 'basic' },
    { key: 'multi', label: 'Multicolor', group: 'land', icon: 'land', match: (r) => r.landType === 'multi' },
    { key: 'rainbow', label: 'Rainbow', group: 'land', icon: 'land', match: (r) => r.landType === 'rainbow' },
    { key: 'fetchLands', label: 'Fetch lands', group: 'land', icon: 'land', match: (r) => r.isFetchLand },
    { key: 'fetchable', label: 'Fetchable', group: 'land', icon: 'land', match: (r) => r.isFetchable },
    { key: 'utilityLands', label: 'Utility lands', group: 'land', icon: 'land', match: (r) => r.isUtilityLand },
    { key: 'tapLands', label: 'Tap lands', group: 'land', icon: 'land', match: (r) => r.isETBTapped },
];

const FILTER_GROUPS = [
    { key: 'mana', label: 'Mana sources - Other' },
    { key: 'land', label: 'Mana Sources - Lands' },
];

const SOURCE_TYPE_LABELS = {
    land: 'Lands',
    rock: 'Mana rocks',
    dork: 'Mana dorks',
    ritual: 'Rituals',
    treasure: 'Treasure',
    landRamp: 'Land ramp',
    other: 'Other',
};

const COLOR_SWATCH = {
    w: { label: 'White', fill: '#f5f3e3', pip: 'w' },
    u: { label: 'Blue', fill: '#4a9bd6', pip: 'u' },
    b: { label: 'Black', fill: '#8a7a9e', pip: 'b' },
    r: { label: 'Red', fill: '#d6534a', pip: 'r' },
    g: { label: 'Green', fill: '#5fa85a', pip: 'g' },
    c: { label: 'Colorless', fill: '#9a9a9a', pip: 'c' },
};


const KeyMetric = StatMetricTile;

export function ManabaseTab({ bin, onSelectCard }) {
    const stats = bin?.manabaseStats;
    const [activeRole, setActiveRole] = useState('fastMana');

    const roleBuckets = useMemo(() => {
        const cards = bin?.cards || [];
        const buckets = {};
        ROLE_FILTERS.forEach((f) => {
            buckets[f.key] = [];
        });
        for (const card of cards) {
            const roles = card?.manabaseRoles;
            if (!roles) continue;
            ROLE_FILTERS.forEach((f) => {
                if (f.match(roles)) buckets[f.key].push(card);
            });
        }
        return buckets;
    }, [bin?.cards]);

    const availableFilters = ROLE_FILTERS.filter((f) => roleBuckets[f.key].length > 0);
    const filteredCards = activeRole ? roleBuckets[activeRole] || [] : [];

    if (!stats) {
        return (
            <Text UNSAFE_className="commanderCardStatsEmpty">
                Manabase metrics aren't available for this bucket yet — they'll populate after the next rebuild.
            </Text>
        );
    }

    const curveData = Object.entries(stats.curve || {})
        .map(([turn, probability]) => ({
            turn: parseInt(turn, 10),
            probability: Math.round((probability || 0) * 100),
        }))
        .sort((a, b) => a.turn - b.turn);

    const histogramData = Object.entries(stats.manaValueHistogram || {})
        .map(([mv, count]) => ({
            manaValue: parseInt(mv, 10),
            avgCards: parseFloat((count || 0).toFixed(2)),
        }))
        .sort((a, b) => a.manaValue - b.manaValue);

    const sourcesData = Object.entries(stats.avgSourcesByType || {})
        .map(([key, count]) => ({
            key,
            label: SOURCE_TYPE_LABELS[key] || key,
            avg: parseFloat((count || 0).toFixed(2)),
        }))
        .filter((row) => row.avg > 0)
        .sort((a, b) => b.avg - a.avg);

    const colorRows = Object.entries(stats.colorProduction || {}).map(([color, data]) => ({
        color,
        meta: COLOR_SWATCH[color] || { label: color.toUpperCase(), fill: '#999' },
        avgReq: parseFloat((data?.avgReq || 0).toFixed(1)),
        avgProd: parseFloat((data?.avgProd || 0).toFixed(1)),
        avgSources: parseFloat((data?.avgSources || 0).toFixed(1)),
    }));

    return (
        <Flex direction="column" rowGap={24} marginTop={10}>
            {/* ----- KEY METRICS GRID ----- */}
            <div className="statsMetricsGrid">
                <KeyMetric label="Avg CMC" value={formatNumber(stats.avgCMC)} hint="nonland cards" />
                <KeyMetric label="Avg lands" value={formatNumber(stats.avgLandCount, 1)} />
                <KeyMetric
                    label="Avg mana sources"
                    value={formatNumber(stats.avgManaSourceCount, 1)}
                    hint="lands + rocks + dorks + rituals + treasure"
                />
                <KeyMetric
                    label="Fast mana"
                    value={formatNumber(stats.avgFastManaCount, 1)}
                    hint="avg count of T0–T1 sources"
                />
                <KeyMetric
                    label="Tap lands"
                    value={formatNumber(stats.avgTapLandCount, 1)}
                    hint="avg ETB-tapped lands"
                />
            </div>

            {/* ----- MANA CURVE ----- */}
            {curveData.length > 0 && (
                <StatsSection
                    title="On-curve castability"
                    subtitle="Avg probability of casting a spell on-curve per turn"
                >
                    <div className="manabaseChartWrap">
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={curveData} margin={{ top: 10, right: 10, left: 0, bottom: 16 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.06)" />
                                <XAxis dataKey="turn" stroke="#8b93a7" tickLine={false}>
                                    <Label value="Turn" offset={-2} position="insideBottom" fill="#8b93a7" />
                                </XAxis>
                                <YAxis
                                    stroke="#8b93a7"
                                    tickLine={false}
                                    domain={[0, 100]}
                                    width={38}
                                    tickFormatter={(v) => `${v}%`}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="probability"
                                    stroke="#e8c600"
                                    strokeWidth={2}
                                    fill="rgba(232, 198, 0, 0.18)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </StatsSection>
            )}

            {/* ----- MANA VALUE HISTOGRAM ----- */}
            {histogramData.length > 0 && (
                <StatsSection
                    title="Mana value distribution"
                    subtitle="Avg nonland card count at each mana value"
                >
                    <div className="manabaseChartWrap">
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={histogramData} margin={{ top: 10, right: 10, left: 0, bottom: 16 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.06)" />
                                <XAxis dataKey="manaValue" stroke="#8b93a7" tickLine={false}>
                                    <Label value="Mana value" offset={-2} position="insideBottom" fill="#8b93a7" />
                                </XAxis>
                                <YAxis stroke="#8b93a7" tickLine={false} width={38} />
                                <Bar dataKey="avgCards" fill="#cc1a1a" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </StatsSection>
            )}

            {/* ----- SOURCE TYPE BREAKDOWN ----- */}
            {sourcesData.length > 0 && (
                <StatsSection
                    title="Mana source breakdown"
                    subtitle="Avg count per source category"
                >
                    <div className="manabaseSourceList">
                        {sourcesData.map((row) => {
                            const max = sourcesData[0]?.avg || 1;
                            const pct = Math.max(4, (row.avg / max) * 100);
                            return (
                                <div key={row.key} className="manabaseSourceRow">
                                    <span className="manabaseSourceLabel">{row.label}</span>
                                    <div className="manabaseSourceBarTrack">
                                        <div className="manabaseSourceBarFill" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="manabaseSourceValue">{row.avg}</span>
                                </div>
                            );
                        })}
                    </div>
                </StatsSection>
            )}

            {/* ----- COLOR PRODUCTION ----- */}
            {colorRows.length > 0 && (
                <StatsSection
                    title="Color production vs requirement"
                    subtitle="Production below requirement signals the average deck is under-producing that color"
                >
                    <div className="manabaseColorList">
                        {colorRows.map((row) => {
                            const deficit = row.avgProd < row.avgReq;
                            const max = Math.max(row.avgReq, row.avgProd, 1);
                            return (
                                <div key={row.color} className="manabaseColorRow">
                                    <div className="manabaseColorHeader">
                                        <span className={`mana medium s${row.color}`} />
                                        <span className="manabaseColorName">{row.meta.label}</span>
                                        {deficit && (
                                            <span className="manabaseColorStatus is-deficit">Under-producing</span>
                                        )}
                                    </div>
                                    <div className="manabaseColorBars">
                                        <div className="manabaseColorBarRow">
                                            <span className="manabaseColorBarLabel">Req</span>
                                            <div className="manabaseColorBarTrack">
                                                <div
                                                    className="manabaseColorBarFill manabaseColorBarFill--req"
                                                    style={{ width: `${(row.avgReq / max) * 100}%` }}
                                                />
                                            </div>
                                            <span className="manabaseColorBarValue">{row.avgReq}</span>
                                        </div>
                                        <div className="manabaseColorBarRow">
                                            <span className="manabaseColorBarLabel">Prod</span>
                                            <div className="manabaseColorBarTrack">
                                                <div
                                                    className="manabaseColorBarFill manabaseColorBarFill--prod"
                                                    style={{
                                                        width: `${(row.avgProd / max) * 100}%`,
                                                        background: row.meta.fill,
                                                    }}
                                                />
                                            </div>
                                            <span className="manabaseColorBarValue">{row.avgProd}</span>
                                        </div>
                                    </div>
                                    <span className="manabaseColorSources">{row.avgSources} avg sources</span>
                                </div>
                            );
                        })}
                    </div>
                </StatsSection>
            )}

            {/* ----- PER-CARD ROLE FILTERS ----- */}
            {availableFilters.length > 0 && (
                <StatsSection
                    title="Manabase cards"
                    subtitle="Pick a role to see which cards in this bucket fill it"
                >
                    <div className="manabaseChipGroups">
                        {FILTER_GROUPS.map((group) => {
                            const chips = availableFilters.filter((f) => f.group === group.key);
                            if (chips.length === 0) return null;
                            return (
                                <div key={group.key} className="manabaseChipGroup">
                                    <span className="manabaseChipGroupLabel">{group.label}</span>
                                    <div className="manabaseChipRow">
                                        {chips.map((f) => {
                                            const count = roleBuckets[f.key].length;
                                            const isActive = activeRole === f.key;
                                            return (
                                                <button
                                                    key={f.key}
                                                    type="button"
                                                    className={`manabaseChip ${isActive ? 'is-active' : ''}`}
                                                    onClick={() => setActiveRole(isActive ? null : f.key)}
                                                >
                                                    {f.icon && (
                                                        <img
                                                            src={`/resources/typeIcons/${f.icon}_symbol.svg`}
                                                            alt=""
                                                            aria-hidden="true"
                                                            className="manabaseChipIcon"
                                                        />
                                                    )}
                                                    <span className="manabaseChipLabel">{f.label}</span>
                                                    <span className="manabaseChipCount">{count}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {activeRole && (
                        <div style={{ marginTop: 12 }}>
                            <CardStatList
                                cards={filteredCards}
                                deckCount={bin.deckCount}
                                onSelectCard={onSelectCard}
                                resetKey={`manabase:${activeRole}:${bin?.binKey}`}
                                emptyMessageUnfiltered="No cards match this role."
                            />
                        </div>
                    )}
                </StatsSection>
            )}
        </Flex>
    );
}
